import { useEffect, useRef } from 'react';
import { lockScroll, unlockScroll } from '../Modal';

const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * While `open`: Tab / Shift+Tab cycle inside `ref`, Escape calls onClose, the page behind
 * does not scroll. On close, focus returns to the element focused before opening (or to
 * `fallbackRef` when that element has gone, e.g. a removed chip).
 */
export default function useFocusTrap(open, ref, onClose, fallbackRef) {
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        if (!open) return undefined;
        const previous = document.activeElement;
        const fallback = fallbackRef?.current;

        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                // Capture phase + stop: an underlying modal must not close too.
                e.preventDefault();
                e.stopPropagation();
                onCloseRef.current();
                return;
            }
            if (e.key !== 'Tab' || !ref.current) return;
            const items = [...ref.current.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);
            if (items.length === 0) {
                e.preventDefault();
                return;
            }
            const first = items[0];
            const last = items[items.length - 1];
            const inside = ref.current.contains(document.activeElement);
            if (e.shiftKey && (document.activeElement === first || !inside)) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && (document.activeElement === last || !inside)) {
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', onKeyDown, true);
        lockScroll();
        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
            unlockScroll();
            const target = previous && document.contains(previous) && previous !== document.body ? previous : fallback;
            target?.focus?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
}
